import { MovementScore } from "../../../models/interfaces/scores/movement-score";
import { FirebaseUtils } from "../../firebase-utils";
import FirestoreServiceAssessments from "../firestore-service-assessments";
import { IUser } from "../../../models/interfaces/assessment-user";
import FirestoreCondition from "../../../models/interfaces/firestore-condition";

const COLLECTION_NAME = "movementScores";

// -----------------------------------------------------------------------------------------
// #region Service Methods
// -----------------------------------------------------------------------------------------

/**
 * Update a recent record, or add a new score to the collection
 * @param {MovementScore} score - The score that is being added to the collection
 * @param {interfaces.User} currentUser - The user that is logged in
 * @returns {Promise<IUser>} A promise to the newly added user
 */
const addOrUpdateRecent = async (score: MovementScore, currentUser: IUser) => {
    // overwrite existing record
    const recents = await FirestoreServiceAssessments.getRecent<MovementScore>(
        COLLECTION_NAME,
        currentUser
    );
    if (recents != null && recents.length >= 1) {
        const recent = recents[0];
        score.id = recent.id;
        score.created = recent.created;
        if (recent.createdBy != null) {
            score.createdBy = recent.createdBy;
        }
        return FirestoreServiceAssessments.update<MovementScore>(
            COLLECTION_NAME,
            score,
            currentUser
        );
    }

    // create record
    score.userId = currentUser.id!;
    return FirestoreServiceAssessments.add<MovementScore>(
        COLLECTION_NAME,
        score,
        currentUser
    );
};

/**
 * Update a recent record, or add a new score to the collection
 * @param {MovementScore} score - The score that is being added to the collection
 * @param {interfaces.User} currentUser - The user that is logged in
 * @returns {Promise<IUser>} A promise to the newly added user
 */
const addOrUpdateRecentById = async (score: MovementScore, currentUserId: string) => {
    // overwrite existing record
    const recents = await FirestoreServiceAssessments.getRecentById<MovementScore>(
        COLLECTION_NAME,
        currentUserId
    );
    if (recents != null && recents.length >= 1) {
        const recent = recents[0];
        score.id = recent.id;
        score.created = recent.created;
        if (recent.createdBy != null) {
            score.createdBy = recent.createdBy;
        }
        return FirestoreServiceAssessments.updateById<MovementScore>(
            COLLECTION_NAME,
            score,
            currentUserId
        );
    }

    // create record
    score.userId = currentUserId;
    return FirestoreServiceAssessments.addById<MovementScore>(
        COLLECTION_NAME,
        score,
        currentUserId
    );
};

/**
 * Delete a user collection by the Id
 * @param {string} id - The Id of the user being deleted
 */
const deleteById = async (id: string) => {
    FirestoreServiceAssessments.deleteById(COLLECTION_NAME, id);
};

/**
 * Delete a user collection by the Id
 * @param {string} id - The Id of the user being deleted
 */
const deleteByUser = async (userId: string) => {
    const records = await FirestoreServiceAssessments.getBy(
        COLLECTION_NAME,
        FirebaseUtils.createFirebaseConditions("userId", "==", userId)
    );

    records.forEach((record) => {
        FirestoreServiceAssessments.deleteById(COLLECTION_NAME, record.id!);
    });
};

/**
 * Find the specific user by the id
 * @param {string} id - The Id of the user that we are retrieving
 * @returns {Promise<MovementScore>} A promise for the user we are retrieving
 */
const get = async (id: string) => {
    return FirestoreServiceAssessments.get<MovementScore>(COLLECTION_NAME, id);
};

/**
 * Get all of the users stored in the database
 * @returns {Promise<MovementScore[]>} A promise for the collection of users
 */
const getAll = async () => {
    return FirestoreServiceAssessments.getAll<MovementScore>(COLLECTION_NAME);
};

/**
 * Find the specific user by the id
 * @param {string} userId - The Id of the user that we are retrieving
 * @returns {Promise<IUser>} A promise for the user we are retrieving
 */
const _getByUserId = async (userId: string) => {
    return FirestoreServiceAssessments.getBy<MovementScore>(
        COLLECTION_NAME,
        FirebaseUtils.createFirebaseConditions("userId", "==", userId)
    );
};

/**
 * Find most recent records by user id
 * @param {string} userId - The Id of the user that we are retrieving records for
 * @param {number} count - Number of records to return
 * @returns {Promise<MovementScore>} A promise for the MSK Score we are retrieving
 */
const getMostRecentByUserId = async (userId: string, count: number = 1) => {
    const conditions: FirestoreCondition[] = [
        {
            field: "userId",
            operator: "==",
            value: userId,
        },
    ];

    return FirestoreServiceAssessments.getBy<MovementScore>(
        COLLECTION_NAME,
        conditions,
        [
            {
                field: "created",
                direction: "desc",
            },
        ],
        count
    );
};

/**
 * Find most recent records by user id
 * @param {string} userId - The Id of the user that we are retrieving records for
 * @param {number} count - Number of records to return
 * @returns {Promise<MovementScore>} A promise for the MSK Score we are retrieving
 */
const getFirstCompletedDateMs = async (
    userId: string
): Promise<number | undefined> => {
    const conditions: FirestoreCondition[] = [
        {
            field: "userId",
            operator: "==",
            value: userId,
        },
    ];

    const movementScores = await FirestoreServiceAssessments.getBy<MovementScore>(
        COLLECTION_NAME,
        conditions,
        [
            {
                field: "created",
                direction: "asc",
            },
        ],
        1
    );
    if (movementScores == null || movementScores.length === 0) {
        return undefined;
    }

    if (movementScores[0].created == null) {
        return 0;
    }
    return
    //TODO
    //return movementScores[0].created.toMillis();
};

// #endregion Service Methods

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const MovementScoreService = {
    addOrUpdateRecent,
    addOrUpdateRecentById,
    deleteById,
    deleteByUser,
    get,
    getAll,
    getFirstCompletedDateMs,
    getMostRecentByUserId,
    _getByUserId,
};

export default MovementScoreService;

// #endregion Exports
